/*============================================================================
  PredictiveSearch — pb theme
  Fetches sections/predictive-search.liquid through the Section Rendering
  API and drops the markup into the panel under the search input. The form
  itself stays a plain GET form, so search still works with JS disabled.
==============================================================================*/

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.enabled = this.dataset.enabled !== 'false';
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('#PbPredictiveResults');
    this.screen = this.querySelector('[data-screen]');
    this.closeBtn = this.querySelector('[data-close]');
    this.abortController = null;

    if (!this.enabled || !this.input || !this.results) return;

    // On typing (debounced)
    this.input.addEventListener(
      'input',
      this.debounce(() => this.onChange(), 300)
    );

    // Re-open existing results when the field regains focus with a term
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim() && this.results.innerHTML.trim()) this.open();
    });

    // Close events
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
    this.screen?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.input.value = '';
      this.close();
      this.input.focus();
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });

    // "View all results" button inside injected markup submits the form,
    // landing on /search?q=<term>. Delegated because the markup is
    // replaced on every fetch.
    this.results.addEventListener('click', (event) => {
      if (event.target.closest('[data-view-all]')) this.form.submit();
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // A newer keystroke cancels the in-flight request, so a slow early
    // response can never overwrite a faster later one.
    this.abortController?.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[limit]': 3,
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    fetch(`${window.Shopify?.routes?.root || '/'}search/suggest?${params}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const markup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.results.innerHTML = markup;
        this.open();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close(true);
      });
  }

  open() {
    this.classList.add('is-active');
    this.input.setAttribute('aria-expanded', 'true');
    if (this.screen) this.screen.hidden = false;
    if (this.closeBtn) this.closeBtn.hidden = false;
  }

  close(clearResults = false) {
    this.classList.remove('is-active');
    this.input.setAttribute('aria-expanded', 'false');
    if (this.screen) this.screen.hidden = true;
    if (this.closeBtn) this.closeBtn.hidden = true;
    if (clearResults) this.results.innerHTML = '';
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
